import { Fragment, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { formatIDR, formatDateID, formatTimeID } from "@/lib/format";
import { Search, ChevronDown, Download, ShoppingBag, Receipt, BarChart3, ChevronLeft, ChevronRight, Users } from "lucide-react";
import { ShopeePeriodFilter, downloadCSV, initPicked, shopeeRange } from "./components";

const TABS = [
  { id: "products", label: "Per Produk" },
  { id: "customers", label: "Per Pelanggan" },
  { id: "txns", label: "Daftar Transaksi" },
];
const CHIPS = [
  { id: "realtime", label: "Hari Ini" },
  { id: "7d", label: "7 Hari" },
  { id: "30d", label: "30 Hari" },
];
const METHOD_LABELS = { cash: "Tunai", qris: "QRIS", transfer: "Transfer", card: "EDC", mixed: "Campuran" };
const CAT_LABELS = { dress: "Dress", outer: "Outer", rok: "Rok", setelan: "Setelan", hijab: "Hijab" };
const CAT_STYLE = {
  dress: "bg-rose-100 text-rose-600",
  outer: "bg-sky-100 text-sky-600",
  rok: "bg-amber-100 text-amber-600",
  setelan: "bg-emerald-100 text-emerald-600",
  hijab: "bg-violet-100 text-violet-600",
};
const PAGE_SIZE = { products: 8, customers: 8, txns: 10 };

function Pager({ total, page, setPage, pageSize, unit, testid }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
      <span className="text-[11px] text-slate-500" data-testid={`${testid}-info`}>Menampilkan {from}–{to} dari {total} {unit}</span>
      <div className="flex items-center gap-1">
        <button data-testid={`${testid}-prev`} onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}
          className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center text-slate-500 disabled:opacity-40 hover:border-[#B2604F] transition-colors">
          <ChevronLeft className="w-3.5 h-3.5" />
        </button>
        {Array.from({ length: pages }, (_, i) => i + 1).slice(0, 7).map((p) => (
          <button key={p} data-testid={`${testid}-page-${p}`} onClick={() => setPage(p)}
            className={`w-8 h-8 rounded-lg text-xs font-semibold transition-colors ${p === page ? "bg-[#B2604F] text-white" : "border border-slate-200 text-slate-600 hover:border-[#B2604F]"}`}>
            {p}
          </button>
        ))}
        <button data-testid={`${testid}-next`} onClick={() => setPage(Math.min(pages, page + 1))} disabled={page === pages}
          className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center text-slate-500 disabled:opacity-40 hover:border-[#B2604F] transition-colors">
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, iconCls, label, value, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5 flex items-center gap-4">
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${iconCls}`}><Icon className="w-5 h-5" /></div>
      <div>
        <div className="text-xs text-slate-500">{label}</div>
        <div data-testid={testid} className="font-display font-bold text-2xl text-slate-900 mt-0.5">{value}</div>
      </div>
    </div>
  );
}

function TxnDetail({ t }) {
  const itemDisc = t.items.reduce((s, i) => s + (i.discount || 0), 0);
  const totalQty = t.items.reduce((s, i) => s + i.qty, 0);
  const gross = t.subtotal + itemDisc;
  const totalDisc = itemDisc + (t.discount_total || 0);
  return (
    <div data-testid={`pos-txn-detail-${t.code}`} className="rounded-2xl border border-slate-200/80 bg-white overflow-hidden shadow-sm">
      <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-slate-50/80 border-b border-slate-100">
        <div className="flex items-center gap-2 min-w-0">
          <Receipt className="w-3.5 h-3.5 text-[#B2604F] flex-shrink-0" />
          <span className="text-xs font-bold text-slate-800 tracking-wide truncate">{t.code}</span>
          <span className="text-[10px] text-slate-400 flex-shrink-0">{formatDateID(t.created_at)} • {formatTimeID(t.created_at)} WIB</span>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {t.status === "voided" && <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-red-50 text-red-500">VOID</span>}
          <span className={`text-[10px] font-semibold px-2.5 py-1 rounded-full ${t.payment_method === "cash" ? "bg-emerald-50 text-emerald-600" : "bg-sky-50 text-sky-600"}`}>{METHOD_LABELS[t.payment_method] || t.payment_method}</span>
        </div>
      </div>
      <table className="w-full">
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
            <th className="text-left font-medium px-4 py-2">Produk</th>
            <th className="text-right font-medium px-3 py-2">Harga Satuan</th>
            <th className="text-center font-medium px-3 py-2">Qty</th>
            <th className="text-right font-medium px-4 py-2">Subtotal</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50">
          {t.items.map((i, idx) => (
            <tr key={idx} className="hover:bg-slate-50/40 transition-colors">
              <td className="px-4 py-2.5">
                <div className="flex items-center gap-3">
                  {i.image
                    ? <img src={i.image} alt={i.name} className="w-10 h-10 rounded-lg object-cover border border-slate-100 flex-shrink-0" />
                    : <div className="w-10 h-10 rounded-lg bg-slate-100 flex-shrink-0" />}
                  <div className="min-w-0">
                    <div className="text-xs font-semibold text-slate-800 truncate">{i.name}</div>
                    {i.variant && <div className="text-[10px] text-slate-400">Variasi: {i.variant}</div>}
                  </div>
                </div>
              </td>
              <td className="px-3 py-2.5 text-right text-xs text-slate-600 whitespace-nowrap">{formatIDR(i.price)}</td>
              <td className="px-3 py-2.5 text-center"><span className="text-[11px] font-semibold text-slate-700 bg-slate-100 rounded-md px-2 py-0.5">×{i.qty}</span></td>
              <td className="px-4 py-2.5 text-right text-xs font-bold text-slate-900 whitespace-nowrap">
                {formatIDR(i.subtotal)}
                {i.discount > 0 && <div className="text-[9px] font-normal text-rose-400">hemat {formatIDR(i.discount)}</div>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="border-t border-slate-100 bg-slate-50/50 px-4 py-2.5 space-y-1">
        <div className="flex justify-between text-[11px] text-slate-500"><span>Subtotal ({totalQty} item)</span><span>{formatIDR(gross)}</span></div>
        {totalDisc > 0 && <div className="flex justify-between text-[11px] text-rose-500"><span>Diskon</span><span>-{formatIDR(totalDisc)}</span></div>}
        <div className="flex justify-between items-baseline pt-1.5 border-t border-dashed border-slate-200">
          <span className="text-[11px] font-semibold text-slate-600">Total Pesanan</span>
          <span className="text-sm font-bold text-[#B2604F]">{formatIDR(t.total)}</span>
        </div>
      </div>
    </div>
  );
}

export default function POSSales() {
  const [tab, setTab] = useState("products");
  const [mode, setMode] = useState("7d");
  const [picked, setPicked] = useState(initPicked());
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [txns, setTxns] = useState([]);
  const [qProd, setQProd] = useState("");
  const [qCust, setQCust] = useState("");
  const [qTxn, setQTxn] = useState("");
  const [cat, setCat] = useState("all");
  const [sortProd, setSortProd] = useState("qty_desc");
  const [custSort, setCustSort] = useState("spend_desc");
  const [openTxn, setOpenTxn] = useState(null);
  const [expandCards, setExpandCards] = useState(new Set());
  const [page, setPage] = useState(1);

  const range = useMemo(() => shopeeRange(mode, picked), [mode, picked]);

  useEffect(() => {
    const params = { date_from: range.from, date_to: range.to };
    api.get("/pos/sales/products", { params }).then((r) => setProducts(r.data));
    api.get("/pos/sales/customers", { params }).then((r) => setCustomers(r.data));
    api.get("/pos/transactions", { params }).then((r) => setTxns(r.data));
  }, [range.from, range.to]);
  useEffect(() => { setPage(1); setOpenTxn(null); }, [tab, qProd, qCust, qTxn, cat, sortProd, custSort, mode, picked]);

  // ---- Produk ----
  const cats = useMemo(() => [...new Set(products.map((p) => p.category).filter(Boolean))], [products]);
  const filteredProducts = useMemo(() => {
    const arr = products.filter((p) =>
      (cat === "all" || p.category === cat) &&
      (!qProd || p.name.toLowerCase().includes(qProd.toLowerCase()) || p.product_id.toLowerCase().includes(qProd.toLowerCase())));
    if (sortProd === "sales_desc") arr.sort((a, b) => b.sales - a.sales);
    else if (sortProd === "txn_desc") arr.sort((a, b) => b.txn_count - a.txn_count);
    else arr.sort((a, b) => b.qty - a.qty);
    return arr;
  }, [products, qProd, cat, sortProd]);
  const prodStats = {
    pcs: filteredProducts.reduce((s, p) => s + p.qty, 0),
    txn: filteredProducts.reduce((s, p) => s + p.txn_count, 0),
    omzet: filteredProducts.reduce((s, p) => s + p.sales, 0),
  };
  const pageProducts = filteredProducts.slice((page - 1) * PAGE_SIZE.products, page * PAGE_SIZE.products);

  // ---- Pelanggan ----
  const filteredCustomers = useMemo(() => {
    const arr = customers.filter((c) => !qCust || (c.name || "").toLowerCase().includes(qCust.toLowerCase())
      || (c.customer_code || "").toLowerCase().includes(qCust.toLowerCase()) || (c.phone || "").includes(qCust));
    if (custSort === "spend_asc") arr.sort((a, b) => a.total_spend - b.total_spend);
    else if (custSort === "txn_desc") arr.sort((a, b) => b.txn_count - a.txn_count);
    else if (custSort === "last_desc") arr.sort((a, b) => (b.last_purchase || "").localeCompare(a.last_purchase || ""));
    else arr.sort((a, b) => b.total_spend - a.total_spend);
    return arr;
  }, [customers, qCust, custSort]);
  const custStats = {
    total: filteredCustomers.length,
    txn: filteredCustomers.reduce((s, c) => s + c.txn_count, 0),
    omzet: filteredCustomers.reduce((s, c) => s + c.total_spend, 0),
  };
  const pageCustomers = filteredCustomers.slice((page - 1) * PAGE_SIZE.customers, page * PAGE_SIZE.customers);
  const custTxnsOf = (c) => txns.filter((t) => (t.customer_id || null) === c.customer_id && t.status !== "voided");

  // ---- Transaksi ----
  const filteredTxns = txns.filter((t) => !qTxn || t.code.toLowerCase().includes(qTxn.toLowerCase()) || (t.customer_name || "").toLowerCase().includes(qTxn.toLowerCase()));
  const pageTxns = filteredTxns.slice((page - 1) * PAGE_SIZE.txns, page * PAGE_SIZE.txns);

  const toggleExpand = (id) => setExpandCards((s) => { const n = new Set(s); if (n.has(id)) { n.delete(id); } else { n.add(id); } return n; });

  const SEARCH = {
    products: { val: qProd, set: setQProd, ph: "Cari nama atau SKU produk..." },
    customers: { val: qCust, set: setQCust, ph: "Cari nama, ID atau WA pelanggan..." },
    txns: { val: qTxn, set: setQTxn, ph: "Cari kode / nama pelanggan..." },
  };
  const SORTS = {
    products: (
      <select data-testid="pos-sales-sort" value={sortProd} onChange={(e) => setSortProd(e.target.value)} className="h-10 px-3 rounded-xl bg-white border border-slate-200 text-xs text-slate-600 focus:outline-none focus:border-[#B2604F]">
        <option value="qty_desc">Urutkan: Terlaris</option>
        <option value="sales_desc">Urutkan: Omzet Tertinggi</option>
        <option value="txn_desc">Urutkan: Transaksi Terbanyak</option>
      </select>
    ),
    customers: (
      <select data-testid="pos-cust-sort" value={custSort} onChange={(e) => setCustSort(e.target.value)} className="h-10 px-3 rounded-xl bg-white border border-slate-200 text-xs text-slate-600 focus:outline-none focus:border-[#B2604F]">
        <option value="spend_desc">Urutkan: Belanja Tertinggi</option>
        <option value="spend_asc">Urutkan: Belanja Terendah</option>
        <option value="txn_desc">Urutkan: Transaksi Terbanyak</option>
        <option value="last_desc">Urutkan: Terakhir Berbelanja</option>
      </select>
    ),
    txns: null,
  };

  const doDownload = () => {
    if (tab === "products") {
      downloadCSV(`pos-penjualan-produk-${range.from}-sd-${range.to}.csv`, ["Produk", "Kategori", "Terjual", "Transaksi", "Omzet"], filteredProducts.map((p) => [p.name, CAT_LABELS[p.category] || p.category || "-", p.qty, p.txn_count, p.sales]));
    } else if (tab === "customers") {
      downloadCSV(`pos-penjualan-pelanggan-${range.from}-sd-${range.to}.csv`, ["Pelanggan", "Customer ID", "Kontak", "Transaksi", "Item", "Total Belanja"], filteredCustomers.map((c) => [c.name, c.customer_code || "-", c.phone || "-", c.txn_count, c.items_qty, c.total_spend]));
    } else {
      downloadCSV(`pos-transaksi-${range.from}-sd-${range.to}.csv`, ["Kode", "Waktu", "Kasir", "Pelanggan", "Metode", "Total"], filteredTxns.map((t) => [t.code, t.created_at, t.cashier_name, t.customer_name || "Umum", METHOD_LABELS[t.payment_method] || t.payment_method, t.total]));
    }
  };

  return (
    <div data-testid="pos-sales-page" className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Penjualan Produk</h1>
          <p className="text-xs text-slate-500">Analisis produk terjual di toko offline</p>
        </div>
        <button data-testid="pos-sales-download" onClick={doDownload}
          className="h-10 px-4 rounded-xl border border-[#B2604F]/40 text-[#B2604F] text-xs font-semibold flex items-center gap-2 hover:bg-[#B2604F]/5 transition-colors">
          <Download className="w-4 h-4" /> Unduh Laporan
        </button>
      </div>

      <div className="flex items-center gap-2 border-b border-slate-200">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`pos-sales-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.id ? "border-[#B2604F] text-[#B2604F]" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
          {CHIPS.map((c) => (
            <button key={c.id} data-testid={`pos-sales-chip-${c.id}`} onClick={() => setMode(c.id)}
              className={`h-8 px-4 rounded-lg text-xs font-medium transition-colors ${mode === c.id ? "bg-[#B2604F] text-white shadow-sm" : "text-slate-600 hover:text-slate-800"}`}>
              {c.label}
            </button>
          ))}
        </div>
        <ShopeePeriodFilter mode={mode} setMode={setMode} picked={picked} setPicked={setPicked} withAll testid="pos-sales-period" />
        {tab === "products" && (
          <select data-testid="pos-sales-cat" value={cat} onChange={(e) => setCat(e.target.value)} className="h-10 px-3 rounded-xl bg-white border border-slate-200 text-xs text-slate-600 focus:outline-none focus:border-[#B2604F]">
            <option value="all">Kategori: Semua</option>
            {cats.map((c) => <option key={c} value={c}>Kategori: {CAT_LABELS[c] || c}</option>)}
          </select>
        )}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="pos-sales-search" value={SEARCH[tab].val} onChange={(e) => SEARCH[tab].set(e.target.value)}
            placeholder={SEARCH[tab].ph}
            className="h-10 w-full pl-9 pr-3 rounded-xl bg-white border border-slate-200 text-xs text-slate-700 focus:outline-none focus:border-[#B2604F]" />
        </div>
        {SORTS[tab]}
      </div>

      {/* Stat cards */}
      {tab === "products" && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <StatCard icon={ShoppingBag} iconCls="bg-rose-50 text-[#B2604F]" label="Total Produk Terjual" value={`${prodStats.pcs.toLocaleString("id-ID")} pcs`} testid="pos-sales-stat-pcs" />
          <StatCard icon={Receipt} iconCls="bg-amber-50 text-amber-500" label="Total Transaksi" value={prodStats.txn.toLocaleString("id-ID")} testid="pos-sales-stat-txn" />
          <StatCard icon={BarChart3} iconCls="bg-emerald-50 text-emerald-500" label="Omzet Penjualan" value={formatIDR(prodStats.omzet)} testid="pos-sales-stat-omzet" />
        </div>
      )}
      {tab === "customers" && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <StatCard icon={Users} iconCls="bg-rose-50 text-[#B2604F]" label="Total Pelanggan" value={custStats.total.toLocaleString("id-ID")} testid="pos-cust-stat-total" />
          <StatCard icon={Receipt} iconCls="bg-amber-50 text-amber-500" label="Total Transaksi" value={custStats.txn.toLocaleString("id-ID")} testid="pos-cust-stat-txn" />
          <StatCard icon={BarChart3} iconCls="bg-emerald-50 text-emerald-500" label="Omzet Penjualan" value={formatIDR(custStats.omzet)} testid="pos-cust-stat-omzet" />
        </div>
      )}
      {tab === "txns" && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <StatCard icon={Receipt} iconCls="bg-rose-50 text-[#B2604F]" label="Total Transaksi" value={filteredTxns.length.toLocaleString("id-ID")} testid="pos-txn-stat-count" />
          <StatCard icon={BarChart3} iconCls="bg-emerald-50 text-emerald-500" label="Omzet Penjualan" value={formatIDR(filteredTxns.filter((t) => t.status !== "voided").reduce((s, t) => s + t.total, 0))} testid="pos-txn-stat-omzet" />
        </div>
      )}

      {/* Per Produk */}
      {tab === "products" && (
        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] text-slate-500 border-b border-slate-100 bg-slate-50/60">
                <th className="px-5 py-3.5 font-medium">Produk</th>
                <th className="px-4 py-3.5 font-medium">Kategori</th>
                <th className="px-4 py-3.5 font-medium text-right">Terjual (pcs)</th>
                <th className="px-4 py-3.5 font-medium text-right">Transaksi</th>
                <th className="px-5 py-3.5 font-medium text-right">Omzet</th>
              </tr>
            </thead>
            <tbody>
              {pageProducts.map((p) => (
                <tr key={p.product_id} data-testid={`pos-sales-product-${p.product_id}`} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      {p.image && <img src={p.image} alt={p.name} className="w-11 h-11 rounded-xl object-cover border border-slate-100" />}
                      <div className="min-w-0">
                        <div className="text-slate-800 font-medium text-xs truncate max-w-xs">{p.name}</div>
                        <div className="text-[10px] text-slate-400">ID: {p.product_id.slice(0, 13)}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {p.category && <span className={`text-[10px] font-semibold px-2.5 py-1 rounded-full ${CAT_STYLE[p.category] || "bg-slate-100 text-slate-500"}`}>{CAT_LABELS[p.category] || p.category}</span>}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-slate-800">{p.qty}</td>
                  <td className="px-4 py-3 text-right text-slate-600">{p.txn_count}</td>
                  <td className="px-5 py-3 text-right font-bold text-slate-900">{formatIDR(p.sales)}</td>
                </tr>
              ))}
              {filteredProducts.length === 0 && (
                <tr><td colSpan={5} className="px-5 py-12 text-center text-xs text-slate-400">Belum ada penjualan pada periode ini.</td></tr>
              )}
            </tbody>
          </table>
          <Pager total={filteredProducts.length} page={page} setPage={setPage} pageSize={PAGE_SIZE.products} unit="produk" testid="pos-sales-pager" />
        </div>
      )}

      {/* Per Pelanggan — kartu pesanan gaya marketplace, detail langsung terlihat */}
      {tab === "customers" && (
        <>
          {filteredCustomers.length === 0 && (
            <div data-testid="pos-cust-empty" className="bg-white rounded-2xl border border-slate-100 px-5 py-14 text-center text-xs text-slate-400">Belum ada penjualan pada periode ini.</div>
          )}
          <div className="space-y-4">
            {pageCustomers.map((c) => {
              const cid = c.customer_id || "umum";
              const orders = custTxnsOf(c);
              const showAll = expandCards.has(cid);
              const shown = showAll ? orders : orders.slice(0, 3);
              return (
                <div key={cid} data-testid={`pos-cust-card-${cid}`} className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
                  {/* Header pelanggan */}
                  <div className="flex items-center gap-3 px-5 py-3 bg-slate-50/60 border-b border-slate-100">
                    <span className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${c.customer_id ? "bg-rose-100 text-[#B2604F]" : "bg-slate-200 text-slate-500"}`}>
                      {(c.name || "P").charAt(0).toUpperCase()}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-sm text-slate-900">{c.name}</span>
                        {c.phone && <span className="text-[10px] text-slate-400">{c.phone}</span>}
                      </div>
                      <div className="text-[10px] text-slate-400">{c.customer_code || "Tanpa Customer ID (Umum)"}</div>
                    </div>
                    <div className="text-right flex-shrink-0 hidden sm:block">
                      <div className="text-xs font-bold text-slate-900">{formatIDR(c.total_spend)}</div>
                      <div className="text-[9px] text-slate-400">{c.txn_count} transaksi • {c.items_qty} item • Terakhir {c.last_purchase ? formatDateID(c.last_purchase) : "—"}</div>
                    </div>
                  </div>
                  {/* Daftar pesanan */}
                  <div className="divide-y divide-slate-50">
                    {orders.length === 0 && <div className="px-5 py-6 text-center text-[11px] text-slate-400">Tidak ada transaksi pada periode ini.</div>}
                    {shown.map((t) => (
                      <div key={t.id} data-testid={`pos-cust-order-${t.code}`} className="px-5 py-3">
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-[11px] text-slate-400">No. Pesanan <span className="font-semibold text-slate-700">{t.code}</span> • {formatDateID(t.created_at)} {formatTimeID(t.created_at)}</span>
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${t.payment_method === "cash" ? "bg-emerald-50 text-emerald-600" : "bg-sky-50 text-sky-600"}`}>{METHOD_LABELS[t.payment_method] || t.payment_method}</span>
                        </div>
                        <div className="mt-1.5 space-y-1.5">
                          {t.items.map((i, idx) => (
                            <div key={idx} className="flex items-center gap-3">
                              {i.image
                                ? <img src={i.image} alt="" className="w-11 h-11 rounded-lg object-cover border border-slate-100 flex-shrink-0" />
                                : <div className="w-11 h-11 rounded-lg bg-slate-100 flex-shrink-0" />}
                              <div className="min-w-0 flex-1">
                                <div className="text-xs font-semibold text-slate-800 truncate">{i.name}</div>
                                {i.variant && <div className="text-[10px] text-slate-400">Variasi: {i.variant}</div>}
                              </div>
                              <span className="text-[11px] text-slate-500 flex-shrink-0">x{i.qty}</span>
                              <span className="text-xs text-slate-500 w-24 text-right flex-shrink-0 hidden sm:block">{formatIDR(i.price)}</span>
                              <span className="text-xs font-bold text-slate-900 w-28 text-right flex-shrink-0">{formatIDR(i.subtotal)}</span>
                            </div>
                          ))}
                        </div>
                        <div className="mt-2 pt-2 border-t border-dashed border-slate-100 flex items-center justify-between">
                          <span className="text-[10px] text-slate-400">{t.cashier_name ? `Kasir: ${t.cashier_name}` : ""}</span>
                          <span className="text-[11px] text-slate-500">Total Pesanan <span className="text-sm font-bold text-[#B2604F] ml-1">{formatIDR(t.total)}</span></span>
                        </div>
                      </div>
                    ))}
                  </div>
                  {orders.length > 3 && (
                    <button data-testid={`pos-cust-more-${cid}`} onClick={() => toggleExpand(cid)}
                      className="w-full py-2.5 text-[11px] font-semibold text-[#B2604F] bg-rose-50/40 hover:bg-rose-50 transition-colors border-t border-slate-100 flex items-center justify-center gap-1">
                      {showAll ? "Sembunyikan" : `Lihat ${orders.length - 3} pesanan lainnya`} <ChevronDown className={`w-3 h-3 transition-transform ${showAll ? "rotate-180" : ""}`} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          {filteredCustomers.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
              <Pager total={filteredCustomers.length} page={page} setPage={setPage} pageSize={PAGE_SIZE.customers} unit="pelanggan" testid="pos-cust-pager" />
            </div>
          )}
        </>
      )}

      {/* Daftar Transaksi */}
      {tab === "txns" && (
        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] text-slate-500 border-b border-slate-100 bg-slate-50/60">
                <th className="px-5 py-3.5 font-medium w-8"></th>
                <th className="px-4 py-3.5 font-medium">Kode</th>
                <th className="px-4 py-3.5 font-medium">Waktu</th>
                <th className="px-4 py-3.5 font-medium">Kasir</th>
                <th className="px-4 py-3.5 font-medium">Pelanggan</th>
                <th className="px-4 py-3.5 font-medium">Metode</th>
                <th className="px-5 py-3.5 font-medium text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {pageTxns.map((t) => (
                <Fragment key={t.id}>
                  <tr data-testid={`pos-txn-${t.code}`} onClick={() => setOpenTxn(openTxn === t.id ? null : t.id)} className="border-b border-slate-50 hover:bg-slate-50/50 cursor-pointer transition-colors">
                    <td className="px-5 py-3"><ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${openTxn === t.id ? "rotate-180" : ""}`} /></td>
                    <td className="px-4 py-3 text-xs font-medium text-slate-800">{t.code} {t.status === "voided" && <span className="ml-1 text-[9px] font-bold px-1.5 py-0.5 rounded bg-red-50 text-red-500">VOID</span>}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{formatDateID(t.created_at)} {formatTimeID(t.created_at)}</td>
                    <td className="px-4 py-3 text-xs text-slate-600">{t.attendant_name || t.cashier_name}</td>
                    <td className="px-4 py-3 text-xs text-slate-600">{t.customer_name || "Umum"}</td>
                    <td className="px-4 py-3 text-xs text-slate-600">{METHOD_LABELS[t.payment_method] || t.payment_method}</td>
                    <td className="px-5 py-3 text-right font-bold text-slate-900">{formatIDR(t.total)}</td>
                  </tr>
                  {openTxn === t.id && (
                    <tr className="border-b border-slate-100 bg-slate-50/40">
                      <td colSpan={7} className="px-12 py-3">
                        <TxnDetail t={t} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
              {filteredTxns.length === 0 && (
                <tr><td colSpan={7} className="px-5 py-12 text-center text-xs text-slate-400">Belum ada transaksi pada periode ini.</td></tr>
              )}
            </tbody>
          </table>
          <Pager total={filteredTxns.length} page={page} setPage={setPage} pageSize={PAGE_SIZE.txns} unit="transaksi" testid="pos-txn-pager" />
        </div>
      )}
    </div>
  );
}
